import React, { useEffect } from 'react';
import { Link } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { ChevronRight } from 'lucide-react';

import type { AppDispatch } from 'src/redux/store';
import { useDataVault } from 'src/contexts/DataVaultContext';
import {
  fetchHistoryCertifications,
  scopeKey,
  selectHistoryCertifications,
} from 'src/redux/slices/DataVault/dataVaultHistoryCertificationsSlice';
import type { CertificationSummary } from 'src/api/dataVault/historyCertifications';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import CertificationHistoryIcon from 'src/icons/CertificationHistoryIcon';
import { certTypeDisplayLabel } from 'src/components/NewRenterInterface/utils/certTerminology';
import { HistoryHubSkeleton } from './HistoryHubSkeleton';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

function iconColors(theme: Theme): { bg: string; fg: string }[] {
  return [
    { bg: '#F0EDFF', fg: '#5B3FBD' }, // even — purple (AI brand)
    {
      bg: alpha(theme.palette.warning.main, 0.12),
      fg: theme.palette.warning.dark,
    }, // odd — orange
  ];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return '';
  }
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// CertCard
// ---------------------------------------------------------------------------

function CertCard({
  cert,
  detailPath,
  index,
}: {
  cert: CertificationSummary;
  detailPath: string;
  index: number;
}): React.ReactElement {
  const theme = useTheme();
  const palette = iconColors(theme)[index % 2];
  const isDone = cert.renter_active === false;
  const date = isDone
    ? formatDate(cert.effective_date ?? cert.submitted_at)
    : null;

  return (
    <Box
      component={Link}
      to={detailPath}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: '14px',
        boxShadow: '0 1px 8px rgba(0,0,0,0.03)',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      {/* Icon circle */}
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: '10px',
          bgcolor: palette.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <CertificationHistoryIcon
          size={20}
          color={palette.fg}
          style={{ marginLeft: '4px' }}
        />
      </Box>

      {/* Text content */}
      <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
        {/* Cert type — bold title */}
        <Typography
          sx={{
            color: 'text.primary',
            fontSize: 13,
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {certTypeDisplayLabel(cert.cert_type_label) || '\u2014'}
        </Typography>

        {/* Effective / completed date */}
        {date ? (
          <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
            {date}
          </Typography>
        ) : null}

        {/* Property name */}
        {cert.property_name ? (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cert.property_name}
          </Typography>
        ) : null}

        {/* Unit + Log number */}
        {cert.unit_label || cert.log_number ? (
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {cert.unit_label ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                Unit {cert.unit_label}
              </Typography>
            ) : null}
            {cert.unit_label && cert.log_number ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                -
              </Typography>
            ) : null}
            {cert.log_number ? (
              <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                Log #{cert.log_number}
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {/* Household name */}
        {cert.household_name ? (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: 12,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {cert.household_name}
          </Typography>
        ) : null}

        {/* Household size */}
        {cert.household_size > 0 ? (
          <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
            {cert.household_size} household member
            {cert.household_size !== 1 ? 's' : ''}
          </Typography>
        ) : null}
      </Stack>

      {/* Chevron */}
      <ChevronRight size={18} color={theme.palette.text.disabled} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// HistoryHubPage
// ---------------------------------------------------------------------------

export function HistoryHubPage(): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>();
  const { actingOnMemberId } = useDataVault();
  const key = scopeKey(actingOnMemberId);
  const entry = useSelector(selectHistoryCertifications(key));

  useEffect(() => {
    if (entry.status === 'loading' || entry.status === 'succeeded') {
      return;
    }
    dispatch(fetchHistoryCertifications({ actingOnMemberId }));
  }, [dispatch, actingOnMemberId, entry.status]);

  function certDetailPath(certId: number): string {
    return actingOnMemberId
      ? `/renter/data-vault/members/${actingOnMemberId}/history/certifications/${certId}`
      : `/renter/data-vault/history/certifications/${certId}`;
  }

  const backPath = actingOnMemberId
    ? `/renter/data-vault/members/${actingOnMemberId}`
    : '/renter/data-vault';

  // Loading state — skeleton
  if (entry.status === 'loading') {
    return <HistoryHubSkeleton />;
  }

  const historyCerts = entry.certifications.filter(
    (cert) => cert.renter_active === false
  );

  return (
    <Box>
      {/* Header */}
      <PageHeader title="History" backRoute={backPath} />

      {/* Error state */}
      {entry.status === 'failed' && (
        <Box role="alert" sx={{ px: 2, pt: 2, textAlign: 'center' }}>
          <Typography sx={{ color: 'text.secondary', fontSize: 14, mb: 1 }}>
            Failed to load applications and re-certifications.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              dispatch(
                fetchHistoryCertifications({
                  actingOnMemberId,
                  forceRefresh: true,
                })
              )
            }
          >
            Retry
          </Button>
        </Box>
      )}

      {/* Empty state */}
      {entry.status === 'succeeded' && historyCerts.length === 0 && (
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 14,
            textAlign: 'center',
            mt: 4,
          }}
        >
          No applications or re-certifications yet.
        </Typography>
      )}

      {/* Certification list */}
      {historyCerts.length > 0 && (
        <Stack
          aria-label="Past applications and re-certifications"
          sx={{ gap: 1.5, px: 2, pt: 2, pb: '100px' }}
        >
          {historyCerts.map((cert, index) => (
            <CertCard
              key={cert.id}
              cert={cert}
              detailPath={certDetailPath(cert.id)}
              index={index}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
